/**
 * AdvancedStep Component
 * Step 5: Advanced editor for power users with guided building blocks
 */

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Code, 
  AlertCircle, 
  CheckCircle, 
  ArrowLeft, 
  Lightbulb, 
  X, 
  Eye, 
  Copy,
  Wand2,
  Building,
  Users,
  ShoppingCart,
  Mail
} from 'lucide-react'
import { validateMacroSyntax, extractKeysFromSyntax } from '../../../utils/macroHelpers'

interface AdvancedStepProps {
  customSyntax: string
  onCustomSyntaxChange: (syntax: string) => void
  onBackToGuided: () => void
  dataKey?: string
  onDataKeyChange?: (dataKey: string) => void
}

interface PatternBlock {
  id: string
  type: 'simple' | 'fallback' | 'conditional' | 'conditional_else'
  dataKey: string
  fallbackValue?: string
  conditionalContent?: string
  elseContent?: string
  text?: string
}

const AdvancedStep: React.FC<AdvancedStepProps> = ({
  customSyntax,
  onCustomSyntaxChange,
  onBackToGuided,
  dataKey = '',
  onDataKeyChange
}) => {
  const [validation, setValidation] = useState<{ isValid: boolean; errors: string[] }>({
    isValid: false,
    errors: []
  })
  const [detectedKeys, setDetectedKeys] = useState<string[]>([])
  const [mode, setMode] = useState<'builder' | 'editor'>('builder')
  const [blocks, setBlocks] = useState<PatternBlock[]>([])
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    if (customSyntax) {
      const validationResult = validateMacroSyntax(customSyntax)
      setValidation(validationResult)
      
      if (validationResult.isValid) {
        setDetectedKeys(extractKeysFromSyntax(customSyntax))
      } else {
        setDetectedKeys([])
      }
    } else {
      setValidation({ isValid: false, errors: [] })
      setDetectedKeys([])
    }
  }, [customSyntax])

  // Enhanced examples organized by use case
  const exampleCategories = [
    {
      title: 'E-commerce Messages',
      icon: ShoppingCart,
      color: 'green',
      examples: [
        {
          title: 'Personalized Order Confirmation',
          syntax: "{{#if first_name}}Hi {{first_name}}, {{else}}Hello, {{/if}}your order {{order_id|'#12345'}} totaling {{order_total|'your items'}} has been confirmed!",
          description: 'Personal greeting with order details and fallbacks',
          useCase: 'Order confirmations, receipts, shipping notifications'
        },
        {
          title: 'Abandoned Cart Recovery',
          syntax: "{{#if first_name}}{{first_name}}, you left something behind! {{else}}Don't forget your items! {{/if}}Complete your {{cart_total|'order'}} purchase and save {{discount|'10%'}}.",
          description: 'Personalized cart recovery with dynamic discount',
          useCase: 'Cart abandonment emails, retargeting messages'
        },
        {
          title: 'Product Recommendation',
          syntax: "{{#if last_purchase}}Based on your {{last_purchase}}, {{else}}We think {{/if}}you might love {{recommended_product|'our featured items'}}. {{#if member_discount}}Use your {{member_discount}} member discount!{{/if}}",
          description: 'Context-aware recommendations with member perks',
          useCase: 'Product suggestions, cross-sell campaigns'
        }
      ]
    },
    {
      title: 'Customer Support',
      icon: Users,
      color: 'blue',
      examples: [
        {
          title: 'Support Ticket Response',
          syntax: "{{#if customer_name}}Hi {{customer_name}}, {{else}}Hello, {{/if}}we've received your request about {{ticket_subject|'your inquiry'}}. {{#if priority}}This {{priority}} priority ticket{{else}}Your ticket{{/if}} will be resolved within {{response_time|'24 hours'}}.",
          description: 'Personalized support with priority handling',
          useCase: 'Support tickets, help desk responses, service updates'
        },
        {
          title: 'Service Status Update',
          syntax: "{{#if service_name}}Your {{service_name}} service {{else}}Your account {{/if}}{{#if status}}is now {{status}}{{else}}has been updated{{/if}}. {{#if next_action}}Next: {{next_action}}{{/if}}",
          description: 'Dynamic service status with next steps',
          useCase: 'Service updates, account changes, maintenance notifications'
        }
      ]
    },
    {
      title: 'Marketing & Engagement',
      icon: Mail,
      color: 'purple',
      examples: [
        {
          title: 'Event Invitation',
          syntax: "{{#if first_name}}{{first_name}}, you're invited! {{else}}Join us! {{/if}}{{event_name|'Our special event'}} on {{event_date|'soon'}}. {{#if vip_status}}As a {{vip_status}} member, you get exclusive perks!{{/if}}",
          description: 'Event invites with VIP treatment',
          useCase: 'Event marketing, webinar invites, exclusive offers'
        },
        {
          title: 'Loyalty Program',
          syntax: "{{#if loyalty_tier}}Welcome back, {{loyalty_tier}} member {{first_name|'valued customer'}}! {{else}}{{first_name|'Hey there'}}, join our loyalty program! {{/if}}You have {{points_balance|'0'}} points. {{#if next_reward}}{{next_reward}} at {{points_needed|'500'}} points!{{/if}}",
          description: 'Tier-based loyalty messaging with progress',
          useCase: 'Loyalty programs, point notifications, tier upgrades'
        }
      ]
    },
    {
      title: 'Business & Enterprise',
      icon: Building,
      color: 'orange',
      examples: [
        {
          title: 'Account Management',
          syntax: "{{#if account_manager}}Your account manager {{account_manager}}{{else}}Our team{{/if}} has {{#if action_taken}}{{action_taken}}{{else}}updated{{/if}} your {{account_type|'business'}} account. {{#if billing_change}}New billing: {{billing_amount|'updated amount'}}.{{/if}}",
          description: 'Professional account updates with billing info',
          useCase: 'B2B communications, account changes, billing updates'
        },
        {
          title: 'System Notification',
          syntax: "{{#if system_name}}{{system_name}}{{else}}System{{/if}} {{notification_type|'update'}}: {{message_body|'Important update available'}}. {{#if action_required}}Action needed by {{deadline|'end of week'}}.{{else}}No action required.{{/if}}",
          description: 'System alerts with conditional action requirements',
          useCase: 'System alerts, maintenance notifications, compliance updates'
        }
      ]
    }
  ]

  // Pattern building blocks
  const patternBlocks = [
    {
      type: 'simple' as const,
      title: 'Simple Replacement',
      description: 'Replace with data value',
      template: '{{key}}',
      icon: '📝'
    },
    {
      type: 'fallback' as const,
      title: 'Fallback Value',
      description: 'Show default if empty',
      template: "{{key|'default'}}",
      icon: '🛡️'
    },
    {
      type: 'conditional' as const,
      title: 'Show If Present',
      description: 'Only show if data exists',
      template: '{{#if key}}content{{/if}}',
      icon: '❓'
    },
    {
      type: 'conditional_else' as const,
      title: 'Show Alternative',
      description: 'Different content for each case',
      template: '{{#if key}}content{{else}}alternative{{/if}}',
      icon: '🔄'
    }
  ]

  // Generate syntax from blocks
  const generateSyntaxFromBlocks = (): string => {
    return blocks.map(block => {
      switch (block.type) {
        case 'simple':
          return `${block.text || ''}{{${block.dataKey}}}`
        case 'fallback':
          return `${block.text || ''}{{${block.dataKey}|'${block.fallbackValue || 'default'}'}}` 
        case 'conditional':
          return `${block.text || ''}{{#if ${block.dataKey}}}${block.conditionalContent || 'content'}{{/if}}`
        case 'conditional_else':
          return `${block.text || ''}{{#if ${block.dataKey}}}${block.conditionalContent || 'content'}{{else}}${block.elseContent || 'alternative'}{{/if}}`
        default:
          return ''
      }
    }).join('')
  }

  // Add a new block
  const addBlock = (type: PatternBlock['type']) => {
    const newBlock: PatternBlock = {
      id: Date.now().toString(),
      type,
      dataKey: '',
      text: ''
    }
    setBlocks([...blocks, newBlock])
  }

  // Remove a block
  const removeBlock = (id: string) => {
    setBlocks(blocks.filter(block => block.id !== id))
  }

  // Update a block
  const updateBlock = (id: string, updates: Partial<PatternBlock>) => {
    setBlocks(blocks.map(block => 
      block.id === id ? { ...block, ...updates } : block
    ))
  }

  // Sync blocks with syntax
  useEffect(() => {
    if (mode === 'builder' && blocks.length > 0) {
      const generatedSyntax = generateSyntaxFromBlocks()
      onCustomSyntaxChange(generatedSyntax)
    }
  }, [blocks, mode])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBackToGuided}
          className="inline-flex items-center text-sm text-[#6E6D7A] hover:text-[#1A2B49] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Return to guided builder
        </button>
        
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
              <Wand2 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white">
                Advanced Pattern Builder
              </h2>
              <p className="text-[#6E6D7A] dark:text-gray-400">
                Combine multiple patterns for complex, dynamic messaging
              </p>
            </div>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setMode('builder')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                mode === 'builder'
                  ? 'bg-white text-[#1A2B49] shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Building className="w-4 h-4 inline mr-1" />
              Builder
            </button>
            <button
              onClick={() => setMode('editor')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                mode === 'editor'
                  ? 'bg-white text-[#1A2B49] shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Code className="w-4 h-4 inline mr-1" />
              Code
            </button>
          </div>
        </div>
      </div>

      {/* Primary Data Key Input */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700"
      >
        <div className="flex items-center mb-4">
          <Code className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            Primary Data Key
          </h3>
        </div>
        
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-4">
          Set the main data key that your advanced pattern will use. You can reference additional keys within your pattern syntax.
        </p>
        
        {onDataKeyChange && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-blue-900 dark:text-blue-200">
              Data Key *
            </label>
            <input
              type="text"
              value={dataKey}
              onChange={(e) => onDataKeyChange(e.target.value)}
              placeholder="e.g., first_name, order_id, customer_email"
              className="w-full px-4 py-3 border border-blue-300 dark:border-blue-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            {dataKey && (
              <div className="flex items-center space-x-2 text-sm text-blue-700 dark:text-blue-300">
                <CheckCircle className="w-4 h-4" />
                <span>Primary key set: <code className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded text-xs">{dataKey}</code></span>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Builder Mode */}
      {mode === 'builder' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Pattern Blocks */}
          <div className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white">
                Build Your Pattern
              </h3>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <Eye className="w-4 h-4 mr-1" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>
            
            {/* Add Pattern Blocks */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {patternBlocks.map((pattern) => (
                <button
                  key={pattern.type}
                  onClick={() => addBlock(pattern.type)}
                  className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 hover:from-purple-50 hover:to-pink-50 border border-gray-200 hover:border-purple-300 rounded-lg text-left transition-all group"
                >
                  <div className="text-lg mb-1">{pattern.icon}</div>
                  <div className="text-sm font-medium text-gray-900 group-hover:text-purple-900">
                    {pattern.title}
                  </div>
                  <div className="text-xs text-gray-600 group-hover:text-purple-700">
                    {pattern.description}
                  </div>
                </button>
              ))}
            </div>

            {/* Current Blocks */}
            {blocks.length > 0 && (
              <div className="space-y-3">
                {blocks.map((block, index) => (
                  <motion.div
                    key={block.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Block {index + 1}:
                        </span>
                        <span className="text-sm font-semibold text-purple-600">
                          {patternBlocks.find(p => p.type === block.type)?.title}
                        </span>
                      </div>
                      <button
                        onClick={() => removeBlock(block.id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Text Before */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Text Before
                        </label>
                        <input
                          type="text"
                          value={block.text || ''}
                          onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                          placeholder="Optional text..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      {/* Data Key */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Data Key *
                        </label>
                        <input
                          type="text"
                          value={block.dataKey}
                          onChange={(e) => updateBlock(block.id, { dataKey: e.target.value })}
                          placeholder="e.g., first_name"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      {/* Additional Fields */}
                      {(block.type === 'fallback' || block.type === 'conditional_else') && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {block.type === 'fallback' ? 'Fallback Value' : 'If Content'}
                          </label>
                          <input
                            type="text"
                            value={block.type === 'fallback' ? (block.fallbackValue || '') : (block.conditionalContent || '')}
                            onChange={(e) => updateBlock(block.id, 
                              block.type === 'fallback' 
                                ? { fallbackValue: e.target.value }
                                : { conditionalContent: e.target.value }
                            )}
                            placeholder={block.type === 'fallback' ? 'Default value' : 'Content when true'}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      )}
                      
                      {block.type === 'conditional' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Content When True
                          </label>
                          <input
                            type="text"
                            value={block.conditionalContent || ''}
                            onChange={(e) => updateBlock(block.id, { conditionalContent: e.target.value })}
                            placeholder="Content to show"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      )}
                    </div>
                    
                    {block.type === 'conditional_else' && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Else Content
                        </label>
                        <input
                          type="text"
                          value={block.elseContent || ''}
                          onChange={(e) => updateBlock(block.id, { elseContent: e.target.value })}
                          placeholder="Content when false"
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
            
            {blocks.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Wand2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Click the pattern blocks above to start building your advanced macro{dataKey ? `. Primary key: ${dataKey}` : ''}</p>
              </div>
            )}
          </div>
          
          {/* Live Preview */}
          {showPreview && blocks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-purple-200 dark:border-purple-700"
            >
              <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-3">
                Live Preview
              </h4>
              <div className="p-3 bg-white dark:bg-gray-800 rounded border font-mono text-sm text-gray-800 dark:text-gray-200">
                {customSyntax || 'Build your pattern above...'}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Editor Mode */}
      {mode === 'editor' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Custom Editor */}
          {/* Custom Editor */}
          <div>
            <label className="block text-sm font-medium text-[#1A2B49] dark:text-white mb-2">
              Custom Macro Syntax
            </label>
            <textarea
              value={customSyntax}
              onChange={(e) => onCustomSyntaxChange(e.target.value)}
              placeholder="e.g., {{#if firstname}}Hey {{firstname|'friend'}}, {{else}}Hello, {{/if}}your order is ready"
              rows={8}
              className={`w-full px-4 py-3 border-2 rounded-lg font-mono text-sm focus:outline-none resize-none transition-colors ${
                customSyntax
                  ? validation.isValid
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 focus:border-green-600'
                    : 'border-red-500 bg-red-50 dark:bg-red-900/20 focus:border-red-600'
                  : 'border-gray-300 dark:border-gray-600 focus:border-[#FE4215] bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
              }`}
            />

            {/* Validation Messages */}
            {customSyntax && validation.errors.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 space-y-2"
              >
                {validation.errors.map((error, index) => (
                  <div key={index} className="flex items-start space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Success Message & Detected Keys */}
            {customSyntax && validation.isValid && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 space-y-3"
              >
                <div className="flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-300 font-medium">Valid syntax!</span>
                </div>

                {detectedKeys.length > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                      Detected Keys ({detectedKeys.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {detectedKeys.map((key) => (
                        <span
                          key={key}
                          className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full border border-blue-300 dark:border-blue-600"
                        >
                          {key}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                      These keys will need data values when you use this macro
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Syntax Reference */}
          <div className="p-5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Lightbulb className="w-4 h-4 mr-2 text-yellow-600" />
              Pattern Reference
            </h3>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <code className="block bg-white dark:bg-gray-700 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 mb-1 text-gray-800 dark:text-gray-200">
                    {`{{key}}`}
                  </code>
                  <p className="text-gray-600 dark:text-gray-400">Simple replacement</p>
                </div>
                <div>
                  <code className="block bg-white dark:bg-gray-700 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 mb-1 text-gray-800 dark:text-gray-200">
                    {`{{key|'default'}}`}
                  </code>
                  <p className="text-gray-600 dark:text-gray-400">With fallback value</p>
                </div>
                <div>
                  <code className="block bg-white dark:bg-gray-700 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 mb-1 text-gray-800 dark:text-gray-200">
                    {`{{#if key}}...{{/if}}`}
                  </code>
                  <p className="text-gray-600 dark:text-gray-400">Conditional block</p>
                </div>
                <div>
                  <code className="block bg-white dark:bg-gray-700 px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 mb-1 text-gray-800 dark:text-gray-200">
                    {`{{#if key}}...{{else}}...{{/if}}`}
                  </code>
                  <p className="text-gray-600 dark:text-gray-400">With alternative</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Real-World Examples */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white flex items-center">
          <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
          Real-World Pattern Examples
        </h3>
        
        {exampleCategories.map((category) => {
          const IconComponent = category.icon
          
          // Get static color classes based on category
          const getColorClasses = (color: string) => {
            switch(color) {
              case 'green':
                return {
                  bg: 'bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20',
                  border: 'border-green-200 dark:border-green-700',
                  text: 'text-green-600',
                  tagBg: 'bg-green-100 dark:bg-green-800',
                  tagText: 'text-green-700 dark:text-green-300',
                  titleText: 'text-green-900 dark:text-green-300'
                }
              case 'blue':
                return {
                  bg: 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20',
                  border: 'border-blue-200 dark:border-blue-700',
                  text: 'text-blue-600',
                  tagBg: 'bg-blue-100 dark:bg-blue-800',
                  tagText: 'text-blue-700 dark:text-blue-300',
                  titleText: 'text-blue-900 dark:text-blue-300'
                }
              case 'purple':
                return {
                  bg: 'bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20',
                  border: 'border-purple-200 dark:border-purple-700',
                  text: 'text-purple-600',
                  tagBg: 'bg-purple-100 dark:bg-purple-800',
                  tagText: 'text-purple-700 dark:text-purple-300',
                  titleText: 'text-purple-900 dark:text-purple-300'
                }
              case 'orange':
                return {
                  bg: 'bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20',
                  border: 'border-orange-200 dark:border-orange-700',
                  text: 'text-orange-600',
                  tagBg: 'bg-orange-100 dark:bg-orange-800',
                  tagText: 'text-orange-700 dark:text-orange-300',
                  titleText: 'text-orange-900 dark:text-orange-300'
                }
              default:
                return {
                  bg: 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20',
                  border: 'border-gray-200 dark:border-gray-700',
                  text: 'text-gray-600',
                  tagBg: 'bg-gray-100 dark:bg-gray-800',
                  tagText: 'text-gray-700 dark:text-gray-300',
                  titleText: 'text-gray-900 dark:text-gray-300'
                }
            }
          }
          
          const colorClasses = getColorClasses(category.color)
          
          return (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-6 ${colorClasses.bg} rounded-lg border ${colorClasses.border}`}
            >
              <div className="flex items-center mb-4">
                <IconComponent className={`w-6 h-6 mr-3 ${colorClasses.text}`} />
                <h4 className={`text-lg font-semibold ${colorClasses.titleText}`}>
                  {category.title}
                </h4>
              </div>
              
              <div className="space-y-4">
                {category.examples.map((example, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        {example.title}
                      </h5>
                      <button
                        onClick={() => onCustomSyntaxChange(example.syntax)}
                        className={`inline-flex items-center px-3 py-1 text-xs ${colorClasses.tagBg} ${colorClasses.tagText} rounded-full hover:opacity-80 transition-opacity`}
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Use Template
                      </button>
                    </div>
                    
                    <code className="block text-xs text-gray-700 dark:text-gray-300 font-mono mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded border break-all">
                      {example.syntax}
                    </code>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <strong>Purpose:</strong> {example.description}
                    </p>
                    
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      <strong>Use cases:</strong> {example.useCase}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Tips */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
        <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
          💡 Best Practices for Complex Patterns
        </h3>
        <ul className="space-y-1 text-xs text-yellow-800 dark:text-yellow-400">
          <li>• <strong>Plan your logic:</strong> Sketch out the different scenarios before building</li>
          <li>• <strong>Use fallbacks liberally:</strong> Always provide defaults for better user experience</li>
          <li>• <strong>Test edge cases:</strong> Consider what happens when data is missing or unexpected</li>
          <li>• <strong>Keep it readable:</strong> Break complex patterns into logical sections</li>
          <li>• <strong>Document your keys:</strong> Note what each data key represents for your team</li>
          <li>• <strong>Preview thoroughly:</strong> Test with realistic data before using in production</li>
        </ul>
      </div>
    </div>
  )
}

export default AdvancedStep
